import { getCurrentInstance, inject } from "vue";

/**
 * Injection key under which the app wrapper provides the host custom element.
 * @see {@link useHostElement}
 */
export const HOST_INJECTION_KEY = Symbol("sp-host-element");

// Sentinel default so a wrapper that provides `null` (e.g. an app mounted
// outside a custom element in tests, where the wrapper's own host is null)
// stays distinguishable from "no provider at all".
const NOT_PROVIDED = Symbol("sp-host-not-provided");

/**
 * Resolves the host custom element for the calling component.
 *
 * Each custom element root component (regular `Sp*` elements, including
 * `Sp*` elements nested inside an app element) has its own host element on
 * `instance.ce`. Returning that directly is what Vue's native `useHost()`
 * does, minus the dev warning it emits when `.ce` is unset; reading the
 * field straight from the instance lets app children resolve a host without
 * triggering that warning.
 *
 * App type elements (registered via `framework/app.js`) render their root
 * component as a child of a synthetic wrapper, so while that root component's
 * setup runs `instance.ce` is undefined. In that case the wrapper's provided host
 * (under `HOST_INJECTION_KEY`) is used. The own host is always preferred
 * over an inherited one so nested custom elements keep targeting their own
 * element (Vue 3.5+ propagates provides across CE app boundaries via DOM
 * ancestry, so without this preference nested `Sp*` elements inside an app
 * would inherit the outer app's host and clobber their own `useExpose`,
 * `useForm`, and `useColorToCssProperty` behaviour).
 *
 * @returns {import('vue').VueElement | null} The host custom element, or null
 * when the component is not running inside a custom element and has no
 * provided host.
 */
export function useHostElement() {
  const instance = getCurrentInstance();
  if (instance?.ce) {
    return instance.ce;
  }
  const injected = inject(HOST_INJECTION_KEY, NOT_PROVIDED);
  return injected === NOT_PROVIDED ? null : injected;
}
